<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <!-- Form Section -->
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- SECTION: Prix & TVA (Hidden when isByPercent) -->
            <template v-if="!isByPercent">
                <!-- Tarif propre à la formule -->
                <div
                    v-if="!isSpecificPrice"
                    class="ppv2-relative"
                    :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20) }"
                >
                    <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                        <BaseSwitch
                            v-model="isSpecificPrice"
                            :label="$t('package.step4.specificPrice')"
                            @change="activeField = 'price'"
                        />
                    </div>
                </div>

                <DashedCard v-else :marginTop="37" :marginBottom="30" :custom-style="{ padding: normalizeSize(24, true) }">
                    <template #title>
                        <BaseSwitch
                            v-model="isSpecificPrice"
                            :label="$t('package.step4.specificPrice')"
                            @change="activeField = 'price'"
                        />
                    </template>
                    <template #content>
                        <!-- Type de tarif : groupe (forfait) vs par participant -->
                        <SegmentedControl
                            v-model="pricing.pricing_type"
                            :label="$t('package.step4.pricingType.label')"
                            :options="pricingTypeOptions"
                            :label-size="13"
                            @update:modelValue="activeField = 'price'"
                        />
                        <p class="ppv2-mb-6 ppv2-text-[12px] poppinsRegular ppv2-text-[#94a3b8]">
                            {{ pricingTypeHint }}
                        </p>

                        <div class="ppv2-grid md:ppv2-grid-cols-2 ppv2-grid-cols-1 ppv2-gap-6 ppv2-w-full">
                            <!-- Prix de vente -->
                            <div>
                                <CustomInput
                                    :label="$t('package.step4.packagePrice')"
                                    v-model="pricing.price"
                                    :right-icon="$symbolCurrency"
                                    :required="true"
                                    :state="getState('price')"
                                    :errorText="errors.price"
                                    type="number"
                                    @input="calculatePriceHT"
                                    @focus="activeField = 'price'"
                                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                    :customSizeLabel="13"
                                    customClassLabel="poppinsMedium"
                                    :floatingLabel="true"
                                    :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                                />
                            </div>

                            <!-- TVA -->
                            <div class="duration-section">
                                <BaseSelect
                                    :label="$t('package.step4.vat')"
                                    v-model="pricing.vat_rate"
                                    :options="vatOptions"
                                    :required="true"
                                    :error="errors.vat_rate"
                                    @input="calculatePriceHT"
                                    @open="activeField = 'price'"
                                    optionLabel="label"
                                    optionValue="value"
                                    :placeholder="$t('common.select')"
                                    class-added="!ppv2-h-[34px] !ppv2-rounded-lg "
                                    :floatingLabel="true"
                                    :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                                />
                            </div>
                        </div>
                    </template>
                </DashedCard>

                <!-- Acompte -->
                <div
                    v-if="!deposite.enabled"
                    class="ppv2-relative"
                    :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20, true) }"
                >
                    <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                        <BaseSwitch
                            v-model="deposite.enabled"
                            :label="$t('experience.step5.multiMode.schedule.accompte')"
                            @change="activeField = 'deposite'"
                        />
                    </div>
                </div>

                <DashedCard v-else :marginTop="37" :marginBottom="30" :custom-style="{ padding: normalizeSize(24, true) }">
                    <template #title>
                        <BaseSwitch
                            v-model="deposite.enabled"
                            :label="$t('experience.step5.multiMode.schedule.accompte')"
                            @change="activeField = 'deposite'"
                        />
                    </template>
                    <template #content>
                        <AcompteForm ref="acompteForm" v-if="deposite.enabled" v-model="depositeData" />
                    </template>
                </DashedCard>
            </template>

            <!-- SECTION: Remise (Shown when isByPercent) -->
            <template v-if="isByPercent">
                <!-- Remise percentage -->
                <div
                    v-if="!isRemiseActive"
                    class="ppv2-relative"
                    :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20) }"
                >
                    <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                        <BaseSwitch v-model="isRemiseActive" :label="$t('package.step4.discountLabel')" />
                    </div>
                </div>

                <DashedCard v-else :marginTop="37" :marginBottom="30" :custom-style="{ padding: normalizeSize(24, true) }">
                    <template #title>
                        <BaseSwitch v-model="isRemiseActive" :label="$t('package.step4.discountLabel')" />
                    </template>
                    <template #content>
                        <div class="ppv2-w-full ppv2-max-w-[300px]">
                            <CustomInput
                                :label="$t('package.step4.discountPercent') || 'Pourcentage de remise'"
                                v-model="pricing.remise_percent"
                                right-icon="%"
                                :required="true"
                                :state="getRemiseState()"
                                :errorText="errors.remise_percent"
                                type="number"
                                :min="0"
                                :max="100"
                                classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg"
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                :customSizeLabel="14"
                                customClassLabel="poppinsMedium"
                                :floatingLabel="true"
                            />
                        </div>
                    </template>
                </DashedCard>
            </template>

            <!-- Validation Errors -->
            <div v-if="errors.pricing" class="error-text ppv2-mb-4">
                {{ errors.pricing }}
            </div>

            <!-- Navigation Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': !isCreationMode }">
                <BackButton v-if="isCreationMode" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="button" @click="submitForm" variant="primary" :loading="loading">
                    {{ isCreationMode ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>

        <!-- Preview Panel -->
        <PreviewPackage :active-field="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import HeaderStep from './HeaderStep.vue'
import PreviewPackage from './PreviewPackage.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import AcompteForm from '../../Experience/components/AcompteForm.vue'
import SegmentedControl from '../../../Shared/SegmentedControl.vue'

export default {
    name: 'PackageStep4',
    components: {
        HeaderStep,
        PreviewPackage,
        BaseButton,
        BackButton,
        DashedCard,
        BaseSwitch,
        BaseSelect,
        CustomInput,
        AcompteForm,
        SegmentedControl,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        isCreationMode: { type: Boolean, default: false },
    },
    data() {
        return {
            activeField: null,
            errors: {},
            initialDataString: null,
            isSpecificPrice: false,
            isRemiseActive: false,
        }
    },
    computed: {
        pricingTypeOptions() {
            return [
                { label: this.$t('package.step4.pricingType.group'), value: 'group' },
                { label: this.$t('package.step4.pricingType.per_person'), value: 'per_person' },
            ]
        },
        pricingTypeHint() {
            return this.pricing.pricing_type === 'per_person'
                ? this.$t('package.step4.pricingType.per_person_hint')
                : this.$t('package.step4.pricingType.group_hint')
        },
        ...mapGetters('package', [
            'pricing',
            'deposite',
            'countryVatRates',
            'isEditMode',
            'isByPercent',
            'selectedType',
            'parameters',
            'loading',
        ]),
        pricing: {
            get() {
                return this.$store.getters['package/pricing']
            },
            set(value) {
                this.$store.dispatch('package/setPricing', value)
            },
        },
        deposite: {
            get() {
                return this.$store.getters['package/deposite']
            },
            set(value) {
                this.$store.dispatch('package/setDeposite', value)
            },
        },
        depositeData: {
            get() {
                return {
                    type: this.deposite.type,
                    value: this.deposite.value,
                }
            },
            set(value) {
                this.setDeposite({
                    ...this.deposite,
                    type: value.type,
                    value: value.value,
                })
            },
        },
        calculatedDeposite() {
            if (!this.deposite.value || !this.pricing.price) return 0
            if (this.deposite.type === 'percentage') {
                return ((this.pricing.price * this.deposite.value) / 100).toFixed(2)
            }
            return this.deposite.value.toFixed(2)
        },
        vatOptions() {
            return this.countryVatRates.map((rate) => ({
                label: `${rate.pourcentage}%`,
                value: parseFloat(rate.pourcentage),
            }))
        },
        parameters: {
            get() {
                return this.$store.getters['package/parameters']
            },
            set(value) {
                this.$store.dispatch('package/setParameters', value)
            },
        },
    },
    watch: {
        'deposite.enabled'(newVal) {
            if (!newVal) {
                this.setDeposite({
                    enabled: false,
                    type: null,
                    value: null,
                })
            }
        },
        // When mode changes (isByPercent), clear the data of the other mode
        isByPercent: {
            handler(newVal) {
                if (newVal) {
                    // Switching to percent mode - clear fixed price data
                    this.setPricing({
                        ...this.pricing,
                        price: 0,
                        price_ht: 0,
                        vat_rate: null,
                    })
                    this.setDeposite({
                        enabled: false,
                        type: null,
                        value: null,
                    })
                    this.isSpecificPrice = false
                } else {
                    // Switching to fixed price mode - enable specific price and clear remise data
                    this.isSpecificPrice = true // Automatically activate "Définir un tarif propre"
                    this.setPricing({
                        ...this.pricing,
                        remise_percent: null,
                    })
                    this.isRemiseActive = false
                }
            },
            immediate: false,
        },
    },
    mounted() {
        if (this.pricing.price || this.pricing.vat_rate) {
            this.isSpecificPrice = true
        }
        if (this.pricing.remise_percent) {
            this.isRemiseActive = true
        }
        this.initialDataString = this.getFormDataString()
    },
    methods: {
        getFormDataString() {
            return JSON.stringify({
                pricing: this.pricing,
                deposite: this.deposite,
                isSpecificPrice: this.isSpecificPrice,
                isRemiseActive: this.isRemiseActive,
            })
        },

        hasChanges() {
            return this.initialDataString !== this.getFormDataString()
        },

        resetInitialState() {
            this.initialDataString = this.getFormDataString()
        },
        ...mapActions('package', ['setPricing', 'setDeposite', 'savePackage', 'setParameters']),
        normalizeSize,
        normalizeSizeVertical,

        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.pricing[field]) return 'valid'
            return 'default'
        },

        getRemiseState() {
            if (this.errors.remise_percent) return 'error'
            if (this.pricing.remise_percent && this.pricing.remise_percent > 0) return 'valid'
            return 'default'
        },

        calculatePriceHT() {
            if (this.pricing.price && this.pricing.vat_rate) {
                const priceHT = this.pricing.price / (1 + this.pricing.vat_rate / 100)
                this.setPricing({ ...this.pricing, price_ht: parseFloat(priceHT.toFixed(5)) })
            }
        },

        calculatePrice() {
            if (this.pricing.price_ht && this.pricing.vat_rate) {
                const price = this.pricing.price_ht * (1 + this.pricing.vat_rate / 100)
                this.setPricing({ ...this.pricing, price: parseFloat(price.toFixed(2)) })
            }
        },

        validateForm() {
            this.errors = {}

            // Validation for fixed price mode (not by percent)
            if (!this.isByPercent && this.isSpecificPrice) {
                if (this.pricing.price === null || this.pricing.price === '' || this.pricing.price < 0) {
                    this.errors.price = this.$t('package.step4.errors.priceRequired')
                }

                if (!this.pricing.vat_rate && this.pricing.vat_rate !== 0) {
                    this.errors.vat_rate = this.$t('package.step4.errors.vatRequired')
                }
            }

            // Validation for remise mode (by percent)
            if (this.isByPercent && this.isRemiseActive) {
                if (!this.pricing.remise_percent || this.pricing.remise_percent <= 0 || this.pricing.remise_percent > 100) {
                    this.errors.remise_percent =
                        this.$t('package.step4.errors.remiseRequired') || 'Le pourcentage de remise doit être entre 1 et 100'
                }
            }

            // Acompte validation only in fixed price mode
            if (!this.isByPercent && this.deposite.enabled) {
                const isValid = this.$refs.acompteForm.validate()
                if (!isValid) return false
            }

            return Object.keys(this.errors).length === 0
        },

        async submitForm() {
            if (!this.validateForm()) {
                return
            }
            // If specific price is disabled, clear pricing data
            if (!this.isSpecificPrice) {
                this.setPricing({
                    price: null,
                    vat_rate: null,
                    price_ht: null,
                    // Sans tarif propre, le type de tarif n'a pas d'objet : on retombe sur le defaut.
                    pricing_type: 'group',
                    code_ean: null,
                })
            }
            try {
                await this.savePackage()
                this.resetInitialState()
                if (!this.isCreationMode) {
                    this.$toast.success(this.$t('package.notifications.success_update'))
                }
                if (this.isCreationMode) {
                    this.$emit('next-step')
                }
            } catch (error) {
                this.$toast.error(error?.response?.data?.message || this.$t('package.notifications.saveErrorDefault'))
            }
        },

        async saveStep(redirect = false) {
            try {
                await this.savePackage()
                this.resetInitialState()
                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                }
            } catch (error) {
                this.$toast.error(error?.response?.data?.message || this.$t('package.notifications.saveErrorDefault'))
            }
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}
</style>
